import { PostalAddressSchema, type PostalAddress } from "#gen/google/type/postal_address_pb.js";
import { clone } from "#lib/protobuf-compat.js";

interface State {
	name: string;
	value: string;
}

export const states: State[] = [
	{
		name: "Alabama",
		value: "AL",
	},
	{
		name: "Alaska",
		value: "AK",
	},
	{
		name: "American Samoa",
		value: "AS",
	},
	{
		name: "Arizona",
		value: "AZ",
	},
	{
		name: "Arkansas",
		value: "AR",
	},
	{
		name: "California",
		value: "CA",
	},
	{
		name: "Colorado",
		value: "CO",
	},
	{
		name: "Connecticut",
		value: "CT",
	},
	{
		name: "Delaware",
		value: "DE",
	},
	{
		name: "District Of Columbia",
		value: "DC",
	},
	{
		name: "Federated States Of Micronesia",
		value: "FM",
	},
	{
		name: "Florida",
		value: "FL",
	},
	{
		name: "Georgia",
		value: "GA",
	},
	{
		name: "Guam",
		value: "GU",
	},
	{
		name: "Hawaii",
		value: "HI",
	},
	{
		name: "Idaho",
		value: "ID",
	},
	{
		name: "Illinois",
		value: "IL",
	},
	{
		name: "Indiana",
		value: "IN",
	},
	{
		name: "Iowa",
		value: "IA",
	},
	{
		name: "Kansas",
		value: "KS",
	},
	{
		name: "Kentucky",
		value: "KY",
	},
	{
		name: "Louisiana",
		value: "LA",
	},
	{
		name: "Maine",
		value: "ME",
	},
	{
		name: "Marshall Islands",
		value: "MH",
	},
	{
		name: "Maryland",
		value: "MD",
	},
	{
		name: "Massachusetts",
		value: "MA",
	},
	{
		name: "Michigan",
		value: "MI",
	},
	{
		name: "Minnesota",
		value: "MN",
	},
	{
		name: "Mississippi",
		value: "MS",
	},
	{
		name: "Missouri",
		value: "MO",
	},
	{
		name: "Montana",
		value: "MT",
	},
	{
		name: "Nebraska",
		value: "NE",
	},
	{
		name: "Nevada",
		value: "NV",
	},
	{
		name: "New Hampshire",
		value: "NH",
	},
	{
		name: "New Jersey",
		value: "NJ",
	},
	{
		name: "New Mexico",
		value: "NM",
	},
	{
		name: "New York",
		value: "NY",
	},
	{
		name: "North Carolina",
		value: "NC",
	},
	{
		name: "North Dakota",
		value: "ND",
	},
	{
		name: "Northern Mariana Islands",
		value: "MP",
	},
	{
		name: "Ohio",
		value: "OH",
	},
	{
		name: "Oklahoma",
		value: "OK",
	},
	{
		name: "Oregon",
		value: "OR",
	},
	{
		name: "Palau",
		value: "PW",
	},
	{
		name: "Pennsylvania",
		value: "PA",
	},
	{
		name: "Puerto Rico",
		value: "PR",
	},
	{
		name: "Rhode Island",
		value: "RI",
	},
	{
		name: "South Carolina",
		value: "SC",
	},
	{
		name: "South Dakota",
		value: "SD",
	},
	{
		name: "Tennessee",
		value: "TN",
	},
	{
		name: "Texas",
		value: "TX",
	},
	{
		name: "Utah",
		value: "UT",
	},
	{
		name: "Vermont",
		value: "VT",
	},
	{
		name: "Virgin Islands",
		value: "VI",
	},
	{
		name: "Virginia",
		value: "VA",
	},
	{
		name: "Washington",
		value: "WA",
	},
	{
		name: "West Virginia",
		value: "WV",
	},
	{
		name: "Wisconsin",
		value: "WI",
	},
	{
		name: "Wyoming",
		value: "WY",
	},
];

export type PostalAddressField = "addressLines" | "locality" | "administrativeArea" | "postalCode";

export function createUpdatedPostalAddress(postalAddress: PostalAddress, field: PostalAddressField, value: string, addressLineIndex: 0 | 1 = 0): PostalAddress {
	const trimmed = value.trim();
	const updated = clone(PostalAddressSchema, postalAddress);
	switch (field) {
		case "addressLines": {
			const lines = updated.addressLines.slice();
			if (trimmed) {
				if (addressLineIndex === 0) {
					lines[0] = trimmed;
				} else if (lines.length === 0) {
					lines.push(trimmed);
				} else {
					lines[1] = trimmed;
				}
			} else if (addressLineIndex === 0 && lines.length > 0) {
				lines.shift();
			} else if (addressLineIndex === 1 && lines.length > 1) {
				lines.splice(1, 1);
			}
			updated.addressLines = lines;
			break;
		}
		case "locality":
			updated.locality = trimmed;
			break;
		case "administrativeArea":
			updated.administrativeArea = trimmed;
			break;
		case "postalCode":
			updated.postalCode = trimmed;
			break;
	}
	return updated;
}

export function formattedStreetAddress(address?: PostalAddress): string {
	if (!address?.addressLines?.length) {
		return "";
	}
	return address!.addressLines
		.map(value => {
			return value?.trim();
		})
		.filter(value => value)
		.join(" ");
}

export function formattedAddress(address?: PostalAddress): string {
	const parts = [];
	const addressLines = formattedStreetAddress(address);
	if (addressLines) {
		parts.push(addressLines);
	}
	const stateParts = [];
	if (address?.locality) {
		stateParts.push(address.locality);
	}
	if (address?.administrativeArea) {
		stateParts.push(address.administrativeArea);
	}
	if (address?.postalCode) {
		stateParts.push(address.postalCode);
	}
	if (stateParts.length > 0) {
		parts.push(stateParts.join(" "));
	}
	return parts.join(", ");
}

export function formattedNeighborhood(address?: PostalAddress): string {
	if (!address) {
		return "";
	}
	const parts: string[] = [];
	if (address.locality && address.locality.length > 0) {
		parts.push(address.locality + (address.administrativeArea || address.postalCode ? "," : ""));
	}
	if (address.administrativeArea && address.administrativeArea.length > 0) {
		parts.push(address.administrativeArea);
	}
	if (address.postalCode && address.postalCode.length > 0) {
		parts.push(address.postalCode);
	}
	return parts.join(" ");
}
